import { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import { useAnmeldung } from "./Anmeldung";
import * as matrix from "../dienst/matrix";

/*
  Der Chat der Fraktion — über Matrix, Ende zu Ende verschlüsselt.

  Warum ein eigener Zustand neben DemoZustand: Die Beiträge liegen nicht mehr
  im Browser, sondern auf dem Matrix-Server. Sie kommen von selbst herein,
  sobald jemand etwas schreibt, und niemand muss die Seite neu laden. Das ist
  ein anderer Rhythmus als bei allem übrigen und gehört deshalb getrennt.

  ---------------------------------------------------------------
  Der Chat darf die App nicht mitreißen
  ---------------------------------------------------------------

  Wenn Matrix klemmt — Server neu gestartet, Netz weg, Verschlüsselung
  streikt —, muss der Rest weiterlaufen: Vorlagen lesen, Notizen schreiben,
  Meinungsbild abgeben. Deshalb steckt hier ein eigener Zustand
  („verbinde“, „bereit“, „fehler“), und die Oberfläche zeigt im Fehlerfall
  einen Satz statt einer leeren Fläche.

  ---------------------------------------------------------------
  Was hier NICHT passiert
  ---------------------------------------------------------------

  Kein Zwischenspeichern der Beiträge in unserer eigenen Datenbank. Das wäre
  bequem für die Suche und würde die Verschlüsselung aushebeln — der Server
  soll die Beiträge gerade nicht lesen können.
*/

const Zusammenhang = createContext(null);

export function ChatProvider({ kanaele, children }) {
  const { token, konto, angemeldet } = useAnmeldung();

  /*
    Das Anmelde-Token steht in einer Referenz, nicht in den Abhängigkeiten
    des Effekts.

    Der Grund ist ein Fehler, der lange gesucht wurde: Beim Start erneuert
    die Anmeldung ihr Token. Hing der Verbindungsaufbau daran, lief er ein
    zweites Mal — und zwar gleichzeitig zum ersten. Der erste legte die
    Schlüsselablage an, der zweite fand sie vor und kannte ihren Schlüssel
    nicht. Ergebnis: „getSecretStorageKey callback returned falsey“, und die
    Schlüsselsicherung kam nie zustande.

    Gebraucht wird das Token nur im Augenblick des Aufrufs. Also wird es dort
    frisch gelesen, statt einen neuen Aufbau auszulösen.
  */
  const tokenRef = useRef(token);
  tokenRef.current = token;

  const [stand, setzeStand] = useState("aus");
  const [fehler, setzeFehler] = useState(null);
  const [raeume, setzeRaeume] = useState({});
  const [nachrichten, setzeNachrichten] = useState([]);

  /*
    Der Wiederherstellungsschlüssel — nur beim allerersten Einrichten gesetzt.
    Er wird genau einmal angezeigt und nirgends gespeichert. Wer ihn verliert
    und alle Geräte verliert, kommt an alte Beiträge nicht mehr heran; der
    Server kann nicht helfen, er kennt sie nicht.
  */
  const [wiederherstellung, setzeWiederherstellung] = useState(null);
  const [kryptofehler, setzeKryptofehler] = useState(null);

  /*
    Dieses Gerät ist neu und noch nicht freigeschaltet: Neue Beiträge gehen,
    ältere bleiben verschlossen. Kein Fehler — eine offene Frage.
  */
  const [freischaltungNoetig, setzeFreischaltungNoetig] = useState(false);

  /* Ein anderes Gerät möchte bestätigt werden. */
  const [anfrage, setzeAnfrage] = useState(null);

  /*
    Die Verbindung liegt in einer Referenz, nicht im Zustand: Sie zu ändern
    darf die Oberfläche nicht neu zeichnen, und sie muss in Rückrufen
    erreichbar sein, die lange vorher angemeldet wurden.
  */
  const verbindung = useRef(null);
  const raumZuKanal = useRef({});

  /*
    Zwei Verbindungsaufbauten dürfen sich nie überlappen — sie schreiben beide
    an denselben Schlüsseln des Kontos.

    Gezählt statt gesperrt: Ein Zähler sagt, welcher Aufbau der jüngste ist.
    Ältere brechen ihre Ergebnisse ab. Eine einfache Sperre hatte den Fehler,
    dass ein abgebrochener Aufbau den nächsten mit aussperrte — dann kam die
    Verbindung überhaupt nicht mehr zustande.
  */
  const laufNummer = useRef(0);

  const aktualisiere = useCallback(() => {
    if (!verbindung.current) return;
    setzeNachrichten(verbindung.current.nachrichten(raumZuKanal.current));
  }, []);

  useEffect(() => {
    if (!angemeldet || !tokenRef.current || !kanaele || !kanaele.length) return undefined;

    laufNummer.current += 1;
    const meinLauf = laufNummer.current;
    const abgeloest = () => laufNummer.current !== meinLauf;

    let abgebrochen = false;
    setzeStand("verbinde");
    setzeFehler(null);

    (async () => {
      try {
        /*
          Erst anmelden: Dabei merkt sich der Browser seine Gerätekennung,
          und der nächste Aufruf kann sie mitschicken, statt ein zweites
          Gerät entstehen zu lassen.
        */
        const zugang = await matrix.holeZugang(tokenRef.current);
        if (abgebrochen || abgeloest()) return;

        /*
          Erst die Räume, dann verbinden. Sonst wäre der erste Durchlauf
          leer, weil die Räume im Moment des Verbindens noch nicht
          existieren.
        */
        const antwort = await matrix.stelleRaeumeSicher(
          tokenRef.current,
          kanaele.map((k) => ({ id: k.id, name: k.name }))
        );
        if (abgebrochen || abgeloest()) return;

        const zuordnung = {};
        (antwort.raeume || []).forEach((r) => {
          zuordnung[r.kanal] = r.raum;
        });
        raumZuKanal.current = zuordnung;
        setzeRaeume(zuordnung);

        /*
          Die Matrix-Zugangsdaten werden für die Passwortabfrage gebraucht,
          mit der Matrix das Einrichten der Verschlüsselung schützt.
        */
        const zugangsdaten = await matrix
          .holeZugangsdaten(tokenRef.current)
          .catch(() => null);
        if (abgebrochen || abgeloest()) return;

        const v = await matrix.verbinde({
          zugang,
          zugangsdaten,
          raeume: zuordnung,
          aufBestaetigungsanfrage: (a) => {
            if (!abgeloest()) setzeAnfrage(a);
          },
          aufAenderung: aktualisiere,
          aufZustand: (neu) => {
            if (!abgebrochen) setzeStand(neu);
          },
        });
        if (abgebrochen || abgeloest()) {
          v.beende();
          return;
        }

        verbindung.current = v;
        if (v.wiederherstellung) setzeWiederherstellung(v.wiederherstellung);
        if (v.kryptofehler) setzeKryptofehler(v.kryptofehler);
        setzeFreischaltungNoetig(Boolean(v.freischaltungNoetig));
        aktualisiere();
      } catch (err) {
        if (!abgebrochen) {
          setzeStand("fehler");
          setzeFehler(err.message || String(err));
        }
      }
    })();

    return () => {
      abgebrochen = true;
      if (verbindung.current) {
        verbindung.current.beende();
        verbindung.current = null;
      }
    };
    /*
      Absichtlich nur an Anmeldung und Kanalzahl gebunden: Ein neuer Aufbau
      der Verbindung ist teuer (Schlüssel, erster Abgleich) und darf nicht
      bei jedem Zeichnen passieren.
    */
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [angemeldet, konto && konto.id, (kanaele || []).length]);

  const sende = useCallback(
    async ({ kanalId, text, topId, annotationId, aufgabeId }) => {
      const raum = raumZuKanal.current[kanalId];
      if (!verbindung.current || !raum) {
        throw new Error("Der Chat ist noch nicht verbunden.");
      }
      await verbindung.current.sende(raum, { text, topId, annotationId, aufgabeId });
    },
    []
  );

  const wert = {
    stand,
    fehler,
    raeume,
    nachrichten,
    sende,
    bereit: stand === "bereit",
    wiederherstellung,
    kryptofehler,
    schluesselNotiert: () => {
      setzeWiederherstellung(null);
      setzeKryptofehler(null);
    },

    /* ---------- Freischaltung eines neuen Geräts ---------- */

    freischaltungNoetig,
    anfrage,
    anfrageErledigt: () => setzeAnfrage(null),

    bestaetigungAnfordern: (rueckrufe) =>
      verbindung.current.bestaetigungAnfordern(rueckrufe),

    mitSchluesselFreischalten: (eingabe) =>
      verbindung.current.mitSchluesselFreischalten(eingabe),

    verlaufHolen: (aufFortschritt) => verbindung.current.verlaufHolen(aufFortschritt),

    meineGeraete: () =>
      verbindung.current ? verbindung.current.meineGeraete() : Promise.resolve([]),
    geraetAbmelden: (id) => verbindung.current.geraetAbmelden(id),

    /*
      Nach erfolgreicher Freischaltung: Das Fenster verschwindet, und der
      Verlauf wird geholt. Beides gehört zusammen — ein freigeschaltetes
      Gerät ohne Verlauf wäre für den Nutzer nicht zu unterscheiden von
      einem, bei dem nichts passiert ist.
    */
    freigeschaltet: async (aufFortschritt) => {
      setzeFreischaltungNoetig(false);
      try {
        await verbindung.current.verlaufHolen(aufFortschritt);
      } catch (fehler) {
        /* Der Verlauf kommt dann beim nächsten Start. */
      }
    },
  };

  return <Zusammenhang.Provider value={wert}>{children}</Zusammenhang.Provider>;
}

export function useChat() {
  const wert = useContext(Zusammenhang);
  if (!wert) throw new Error("useChat muss innerhalb von ChatProvider verwendet werden.");
  return wert;
}
